import React, { useEffect, useRef, useState } from "react";
import DataGrid, { Column, Editing, Lookup } from "devextreme-react/data-grid";
import {
  Button,
  LoadPanel,
  SelectBox,
  TagBox,
  TextBox,
} from "devextreme-react";
import { ToastDisplayer } from "../../../components/toastDisplay/ToastDisplayer";
import {
  getStatusApi,
  addStatusApi,
  editStatusApi,
  deleteStatusApi,
} from "../../../api/settingsApi";
import "./StatusTab.scss";

export default function StatusTab() {
  const [loading, setLoading] = useState(false);
  const [statusList, setStatusList] = useState([]);
  const [statusInput, setStatusInput] = useState("");
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editRowData, setEditRowData] = useState({});
  const dataGridRef = useRef(null);
  // Edit mode state
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  const colorPalette = [
    { name: "Red", hex: "#FF0000" },
    { name: "Green", hex: "#008000" },
    { name: "Blue", hex: "#0000FF" },
    { name: "Yellow", hex: "#FFFF00" },
    { name: "Orange", hex: "#FFA500" },
    { name: "Purple", hex: "#800080" },
    { name: "Cyan", hex: "#00FFFF" },
    { name: "Magenta", hex: "#FF00FF" },
    { name: "Lime", hex: "#00FF00" },
    { name: "Pink", hex: "#FFC0CB" },
    { name: "Teal", hex: "#008080" },
    { name: "Lavender", hex: "#E6E6FA" },
    { name: "Brown", hex: "#A52A2A" },
    { name: "Beige", hex: "#F5F5DC" },
    { name: "Maroon", hex: "#800000" },
    { name: "Mint", hex: "#98FF98" },
    { name: "Olive", hex: "#808000" },
    { name: "Coral", hex: "#FF7F50" },
    { name: "Navy", hex: "#000080" },
    { name: "Grey", hex: "#808080" },
    { name: "White", hex: "#FFFFFF" },
    { name: "Black", hex: "#000000" },
    { name: "Gold", hex: "#FFD700" },
    { name: "Silver", hex: "#C0C0C0" },
    { name: "Indigo", hex: "#4B0082" },
    { name: "Turquoise", hex: "#40E0D0" },
    { name: "Violet", hex: "#EE82EE" },
    { name: "Salmon", hex: "#FA8072" },
    { name: "Chocolate", hex: "#D2691E" },
    { name: "Crimson", hex: "#DC143C" },
  ];

  const renderColorItem = (data) => {
    if (!data) return null;

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span
          style={{
            width: "12px",
            height: "12px",
            borderRadius: "50%",
            backgroundColor: data.hex,
          }}
        />
        <span>{data.name}</span>
      </div>
    );
  };

  const renderColorButton = () => {
    const selected = colorPalette.find((c) => c.hex === selectedColor);

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <span
          style={{
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            backgroundColor: selected?.hex || "#ccc",
          }}
        />
        <i className="dx-icon dx-icon-chevron-down" />
      </div>
    );
  };

  // ── FETCH all statuses ──────────────────────────────────────────────────
  const fetchStatuses = async () => {
    try {
      setLoading(true);
      const data = await getStatusApi();
      setStatusList(data.data || data);
    } catch (error) {
      ToastDisplayer(
        "error",
        error?.response?.data?.message || "Failed to fetch statuses.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatuses();
  }, []);

  // const handleRowUpdating = (e) => {
  //   console.log("Hiii");
  //   // assign a Promise to e.cancel — DevExtreme waits for it
  //   e.cancel = new Promise(async (resolve) => {
  //     try {
  //       setLoading(true);

  //       const payload = {
  //         status_id: e.key,
  //         name: e.newData?.name ?? e.oldData?.name,
  //         color_code: editRowData.color_code ?? e.oldData?.color_code,
  //         allowed_roles: editRowData.allowed_roles ?? e.oldData?.allowed_roles,
  //       };

  //       console.log("Payload:", payload); // verify

  //       await editStatusApi(payload);

  //       ToastDisplayer("success", "Status updated successfully!");
  //       setEditingRowId(null);
  //       setEditRowData({});
  //       await fetchStatuses();

  //       resolve(false); //false = allow DevExtreme to exit edit mode
  //     } catch (err) {
  //       ToastDisplayer("error", "Update failed");
  //       resolve(true); // true = cancel save, stay in edit mode
  //     } finally {
  //       setLoading(false);
  //     }
  //   });
  // };

  const handleRowRemoving = (e) => {
    e.cancel = new Promise(async (resolve) => {
      try {
        setLoading(true);

        await deleteStatusApi(e.key);

        ToastDisplayer("success", "Deleted successfully");
        resolve(false); //  allow delete
      } catch (err) {
        ToastDisplayer(
          "error",
          err?.error || "Cannot delete status assigned to tickets",
        );

        resolve(true); // cancel delete
      } finally {
        setLoading(false);
      }
    });
  };

  // ── ADD status ──────────────────────────────────────────────────────────
  const handleAddStatus = async () => {
    if (!statusInput.trim()) {
      ToastDisplayer("error", "Please enter a status name.");
      return;
    }

    if (!selectedColor) {
      ToastDisplayer("error", "Please select a color.");
      return;
    }

    if (!selectedRoles.length) {
      ToastDisplayer("error", "Please select at least one role.");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        name: statusInput,
        color_code: selectedColor, // from SelectBox
        allowed_roles: selectedRoles, // from TagBox
      };

      // return console.log("Payload : ",payload);

      const res = await addStatusApi(payload);

      if (res?.data?.error) {
        return ToastDisplayer("error", res.data.error);
      }

      ToastDisplayer("success", "Status added successfully!");

      // reset
      setStatusInput("");
      setSelectedColor(null);
      setSelectedRoles([]);

      fetchStatuses();
    } catch (error) {
      ToastDisplayer(
        "error",
        error?.response?.data?.message || "Failed to add status.",
      );
    } finally {
      setLoading(false);
    }
  };

  // ── START editing a row ─────────────────────────────────────────────────
  const handleEditClick = (item) => {
    setEditingId(item.id);
    setEditingValue(item.name);
  };

  // ── SAVE edited status ──────────────────────────────────────────────────
  const handleEditSave = async (id) => {
    if (!editingValue.trim()) {
      ToastDisplayer("error", "Status name cannot be empty.");
      return;
    }

    try {
      setLoading(true);

      await editStatusApi({
        status_id: id,
        name: editingValue,
      });

      ToastDisplayer("success", "Status updated successfully!");
      setEditingId(null);
      setEditingValue("");
      fetchStatuses();
    } catch (error) {
      ToastDisplayer(
        "error",
        error?.response?.data?.message || "Failed to update status.",
      );
    } finally {
      setLoading(false);
    }
  };

  // ── CANCEL editing ──────────────────────────────────────────────────────
  const handleEditCancel = () => {
    setEditingId(null);
    setEditingValue("");
  };

  return (
    <>
      {loading && <LoadPanel visible={true} />}

      <div className="statusTabSec">
        {/* ── Categories-style collapsible section ── */}
        <div className="statusTabCard">
          <div className="statusCardHead">
            <span className="statusCardTitle">Manage Status Names</span>
          </div>

          {/* Input row — same layout as Category Name input */}
          <div className="statusCardBody">
            <div className="catInputRow">
              <div className="catInputWrap">
                <label className="catLabel">
                  Status Name <span className="required">*</span>
                </label>
                <TextBox
                  height={48}
                  placeholder="Enter Status Name"
                  stylingMode="outlined"
                  value={statusInput}
                  onValueChanged={(e) => setStatusInput(e.value)}
                  valueChangeEvent="keyup"
                />
              </div>

              <div className="catInputWrap">
                <label className="catLabel">
                  Assigned to <span className="required">*</span>
                </label>
                <TagBox
                  height={48}
                  width={178}
                  className="tagboxScroll"
                  value={selectedRoles}
                  valueExpr="value"
                  displayExpr="text"
                  placeholder="Select Roles"
                  showSelectionControls={true}
                  items={[
                    { text: "company", value: "company" },
                    { text: "companyuser", value: "companyuser" },
                    { text: "customer", value: "customer" },
                  ]}
                  stylingMode="outlined"
                  onValueChanged={(e) => setSelectedRoles(e.value)}
                />
              </div>
              <div className="colorPickerWrap">
                <label htmlFor="colorPicker" className="catLabel">
                  Choose Status Color <span className="required">*</span>
                </label>
                <SelectBox
                  height={48}
                  width={183}
                  value={selectedColor}
                  valueExpr="hex"
                  displayExpr="name"
                  placeholder="Select Status Color"
                  items={colorPalette}
                  stylingMode="outlined"
                  onValueChanged={(e) => {
                    setSelectedColor(e.value); // this is hex
                  }}
                  itemRender={renderColorItem}
                  dropDownButtonRender={renderColorButton}
                />
              </div>
              <Button
                text="ADD STATUS"
                height={45}
                type="default"
                onClick={handleAddStatus}
              />
            </div>
            <DataGrid
              ref={dataGridRef}
              style={{ height: "calc(100vh - 477px)" }}
              dataSource={statusList}
              keyExpr="id"
              showBorders={true}
              columnAutoWidth={true}
              // onRowUpdating={handleRowUpdating}
              onRowRemoving={handleRowRemoving}
              onEditingStart={(e) => {
                setEditingRowId(e.key); //  mark which row is editing
                setEditRowData({}); //  reset tracked changes
              }}
              onEditCanceled={() => {
                setEditingRowId(null); //  clear on cancel
                setEditRowData({});
              }}
            >
              {/* Editing */}
              <Editing
                mode="row"
                allowUpdating={true}
                allowDeleting={true}
                useIcons={true}
              />
              {/* Status Name */}
              <Column
                // width={250}
                dataField="name"
                caption="Status Name"
                alignment="left"
              />
              {/* Color Column */}
              <Column
                dataField="color_code"
                caption="Color"
                // width="250px"
                allowEditing={false} // tell DevExtreme — WE handle this
                cellRender={(cellData) => {
                  const isEditing = editingRowId === cellData.data.id;
                  const currentValue = isEditing
                    ? (editRowData.color_code ?? cellData.value)
                    : cellData.value;

                  return isEditing ? (
                    // EDIT MODE — fully controlled SelectBox
                    <SelectBox
                      dataSource={colorPalette}
                      valueExpr="hex"
                      displayExpr="name"
                      stylingMode="outlined"
                      value={currentValue}
                      applyValueMode="instantly"
                      onValueChanged={(e) => {
                        setEditRowData((prev) => ({
                          ...prev,
                          color_code: e.value, // track in our own state
                        }));
                      }}
                      itemRender={renderColorItem}
                    />
                  ) : (
                    // console.log(cellData.value)
                    // VIEW MODE — simple display
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span
                        style={{
                          width: "14px",
                          height: "14px",
                          borderRadius: "50%",
                          backgroundColor: cellData.value,
                          border: "1px solid #a5a0a0",
                        }}
                      />
                      <span>{cellData.value}</span>
                    </div>
                  );
                }}
              />
              {/* Roles Column */}
              {/* <Column
                // width={500}
                dataField="allowed_roles"
                caption="Roles"
                allowEditing={false} // tell DevExtreme — WE handle this
                cellRender={(cellData) => {
                  const isEditing = editingRowId === cellData.data.id;
                  const currentValue = isEditing
                    ? (editRowData.allowed_roles ?? cellData.value ?? [])
                    : (cellData.value ?? []);

                  return isEditing ? (
                    // EDIT MODE — fully controlled TagBox
                    <TagBox
                      dataSource={[
                        { text: "company", value: "company" },
                        { text: "companyuser", value: "companyuser" },
                        { text: "customer", value: "customer" },
                      ]}
                      valueExpr="value"
                      displayExpr="text"
                      stylingMode="outlined"
                      value={currentValue}
                      showSelectionControls={true}
                      applyValueMode="instantly"
                      onValueChanged={(e) => {
                        setEditRowData((prev) => ({
                          ...prev,
                          allowed_roles: e.value, // track in our own state
                        }));
                      }}
                    />
                  ) : (
                    <span>{currentValue.join(", ")}</span>
                  );
                }}
              /> */}
              <Column
                caption="Quantum Admin"
                alignment="center"
                cellRender={(cellData) => {
                  const role = "company";
                  const isEditing = editingRowId === cellData.data.id;

                  const roles = isEditing
                    ? (editRowData.allowed_roles ??
                      cellData.data.allowed_roles ??
                      [])
                    : (cellData.data.allowed_roles ?? []);

                  const isChecked = roles.includes(role);

                  return (
                    <input
                      className="custom-checkbox"
                      type="checkbox"
                      checked={isChecked}
                      disabled={!isEditing}
                      onChange={(e) => {
                        if (!isEditing) return;

                        let updatedRoles = [...roles];

                        if (e.target.checked) {
                          updatedRoles.push(role);
                        } else {
                          updatedRoles = updatedRoles.filter((r) => r !== role);
                        }

                        setEditRowData((prev) => ({
                          ...prev,
                          allowed_roles: updatedRoles,
                        }));
                      }}
                    />
                  );
                }}
              />
              <Column
                caption="Quantum Users"
                alignment="center"
                cellRender={(cellData) => {
                  const role = "companyuser";
                  const isEditing = editingRowId === cellData.data.id;

                  const roles = isEditing
                    ? (editRowData.allowed_roles ??
                      cellData.data.allowed_roles ??
                      [])
                    : (cellData.data.allowed_roles ?? []);

                  const isChecked = roles.includes(role);

                  return (
                    <input
                      className="custom-checkbox"
                      type="checkbox"
                      checked={isChecked}
                      disabled={!isEditing}
                      onChange={(e) => {
                        if (!isEditing) return;

                        let updatedRoles = [...roles];

                        if (e.target.checked) {
                          updatedRoles.push(role);
                        } else {
                          updatedRoles = updatedRoles.filter((r) => r !== role);
                        }

                        setEditRowData((prev) => ({
                          ...prev,
                          allowed_roles: updatedRoles,
                        }));
                      }}
                    />
                  );
                }}
              />
              <Column
                caption="Client"
                alignment="center"
                cellRender={(cellData) => {
                  const role = "customer";
                  const isEditing = editingRowId === cellData.data.id;

                  const roles = isEditing
                    ? (editRowData.allowed_roles ??
                      cellData.data.allowed_roles ??
                      [])
                    : (cellData.data.allowed_roles ?? []);

                  const isChecked = roles.includes(role);

                  return (
                    <input
                      className="custom-checkbox"
                      type="checkbox"
                      checked={isChecked}
                      disabled={!isEditing}
                      onChange={(e) => {
                        if (!isEditing) return;

                        let updatedRoles = [...roles];

                        if (e.target.checked) {
                          updatedRoles.push(role);
                        } else {
                          updatedRoles = updatedRoles.filter((r) => r !== role);
                        }

                        setEditRowData((prev) => ({
                          ...prev,
                          allowed_roles: updatedRoles,
                        }));
                      }}
                    />
                  );
                }}
              />
              <Column
                type="buttons"
                buttons={[
                  {
                    name: "edit",
                    visible: (e) => editingRowId !== e.row.data.id,
                  },
                  {
                    name: "delete",
                    visible: (e) => editingRowId !== e.row.data.id,
                  },
                  {
                    name: "save", // override built-in save — hide it
                    visible: false,
                  },
                  {
                    name: "cancel", // override built-in cancel — hide it
                    visible: false,
                  },
                  {
                    icon: "save",
                    visible: (e) => editingRowId === e.row.data.id, // show only when editing
                    onClick: (e) => {
                      const rowData = e.row.data;

                      const payload = {
                        status_id: rowData.id,
                        name: rowData.name,
                        color_code:
                          editRowData.color_code ?? rowData.color_code,
                        allowed_roles:
                          editRowData.allowed_roles ?? rowData.allowed_roles,
                      };

                      if (!payload.name?.trim()) {
                        ToastDisplayer("error", "Status name is required");
                        return;
                      }

                      if (!payload.color_code) {
                        ToastDisplayer("error", "Color is required");
                        return;
                      }

                      if (
                        !payload.allowed_roles ||
                        payload.allowed_roles.length === 0
                      ) {
                        ToastDisplayer(
                          "error",
                          "At least one role must be selected",
                        );
                        return;
                      }

                      // console.log("Payload:", payload);

                      setLoading(true);

                      editStatusApi(payload)
                        .then(() => {
                          ToastDisplayer(
                            "success",
                            "Status updated successfully!",
                          );
                          setEditingRowId(null);
                          setEditRowData({});
                          dataGridRef.current?.instance?.cancelEditData();
                          fetchStatuses();
                        })
                        .catch(() => {
                          ToastDisplayer("error", "Update failed");
                        })
                        .finally(() => {
                          setLoading(false);
                        });
                    },
                  },
                  {
                    icon: "revert",
                    visible: (e) => editingRowId === e.row.data.id, // show only when editing
                    onClick: () => {
                      dataGridRef.current?.instance?.cancelEditData();
                      setEditingRowId(null);
                      setEditRowData({});
                    },
                  },
                ]}
              />
            </DataGrid>
          </div>
        </div>
      </div>
    </>
  );
}
